import React from "react";
import { Box, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { InterviewRecording } from "../../../models/PresentationDraft";
import { formatDate } from "../../../utils/formatting";

interface FirefliesInterviewPickerProps {
	readonly interviews: InterviewRecording[];
	readonly selectedId?: string;
	readonly isImporting: boolean;
	readonly matchedByName?: boolean;
	readonly onSelect: (interview: InterviewRecording) => void;
}

const FirefliesInterviewPicker: React.FC<FirefliesInterviewPickerProps> = ({
	interviews,
	selectedId,
	isImporting,
	matchedByName = false,
	onSelect
}) => {
	const { t } = useTranslation();

	const describe = (interview: InterviewRecording) =>
		t("presentationDraft.start.fireflies.option", {
			date: formatDate(interview.recordedAt),
			minutes: interview.durationMinutes
		});

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
				<TextField
					select
					size="small"
					label={t("presentationDraft.start.fireflies.label")}
					value={selectedId ?? ""}
					disabled={isImporting}
					onChange={event => {
						const interview = interviews.find(
							candidate => candidate.id === event.target.value
						);
						if (interview) onSelect(interview);
					}}
					sx={{ minWidth: 260 }}
					slotProps={{ inputLabel: { shrink: true } }}
				>
					{interviews.map(interview => (
						<MenuItem key={interview.id} value={interview.id}>
							<Box>
								<Typography variant="body2">{describe(interview)}</Typography>
								<Typography variant="caption" color="text.secondary">
									{interview.title}
								</Typography>
							</Box>
						</MenuItem>
					))}
				</TextField>
				{isImporting && (
					<>
						<CircularProgress size={16} color="secondary" />
						<Typography variant="caption" color="text.secondary">
							{t("presentationDraft.start.fireflies.importing")}
						</Typography>
					</>
				)}
			</Box>
			{matchedByName && (
				<Typography variant="caption" color="warning.main">
					{t("presentationDraft.start.fireflies.matchedByName")}
				</Typography>
			)}
		</Box>
	);
};

export default FirefliesInterviewPicker;
